import React, { useState, useEffect } from "react";
import { Row, Col, Card, Form, Button, Badge, Modal, Spin } from "antd";
import Scrollspy from "react-scrollspy";
import "../../../custom/scrollspy.css";
import { PersonalInformation } from "./PersonalInformation";
import { DocumentUpload } from "./DocumentUpload";
import { JobsApplied } from "./JobsApplied";
import { SavedApplication } from "./SavedApplication";
import { useSelector, useDispatch } from "react-redux";
import { UpdateUser, SetAppliedJobs } from "../../../actions";
import {
  UserOutlined,
  FolderOutlined,
  IdcardOutlined,
  ContainerOutlined,
} from "@ant-design/icons";
import { GlobalApiErrorHandler, string_trim } from "../../../shared/helpers";
import { BrowserView, isBrowser, MsgBox } from "../../../shared/custom-ant-d";
import {
  AppStatus,
  isAppliedJobs,
  isSavedJobs,
} from "../../../constants/AppStatus";
import {
  reqGetDPCSText,
  reqPostUpdateCandidateProfile,
  reqPostRetrieveCandidateProfile,
} from "../../../axios/api";

const layout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const validateMessages = {
  required: "${label} is required.",
};

export const Profile = () => {
  const currentPath = window.location.pathname;
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const Auth = useSelector((state) => state.Auth);
  //const JobList = useSelector(state => state.AppliedJobs);
  const LookUpData = useSelector((state) => state.LookUpData);

  const [profileSaveLoading, setProfileSaveLoading] = useState(false);
  const [countries, setCountries] = useState(null);
  const [preferredContactMethodList, setPreferredContactMethodList] =
    useState(null);
  const [statesList, setStatesList] = useState(null);
  const [phoneMinChar, setPhoneMinChar] = useState(10);
  const [statesOfCountry, setStateOfCountry] = useState(null);
  const [selectedCountryName, setSelectedCountryName] = useState(
    Auth.countryName
  );

  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isJobApplicationLoading, setIsJobApplicationLoading] = useState(true);
  const [jobApplicationCount, setJobsApplicationCount] = useState(0);
  const [jobSavedDraftList, setJobSavedDraftList] = useState(null);
  const [zipRequirments, setZipRequirements] = useState("");
  const [candidateObj, setCandidateObj] = useState(null);
  const [termContent, setTermContent] = useState("");
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);

  const statesAsProps = {
    profileSaveLoading,
    setProfileSaveLoading,
    countries,
    setCountries,
    preferredContactMethodList,
    setPreferredContactMethodList,
    statesList,
    setStatesList,
    phoneMinChar,
    setPhoneMinChar,
    statesOfCountry,
    setStateOfCountry,
    selectedCountryName,
    setSelectedCountryName,
    isProfileLoading,
    zipRequirments,
  };

  useEffect(() => {
    if (LookUpData !== null) {
      getLookupData();
    }
  }, [LookUpData, Auth]);

  useEffect(() => {
    setIsProfileLoading(true);

    if (Auth.isLoggedIn) {
      getProfileDetails();
    }
  }, [Auth.isLoggedIn]);

  const jobApplicationsCounter = (jobApplicationLists) => {
    if (jobApplicationLists) {
      const tempJobApplications = jobApplicationLists.filter((item, i) => {
        return item.appStatus !== AppStatus.Withdrawn;
      });

      if (tempJobApplications.length > 0) {
        setJobsApplicationCount(tempJobApplications.length);
      } else {
        setJobsApplicationCount(0);
      }
    }
  };

  const getLookupData = () => {
    if (LookUpData) {
      setCountries(LookUpData.countryList);
      setPreferredContactMethodList(LookUpData.preferredContactMethodList);
      setStatesList(LookUpData.statesList);
    }

    try {
      var statesToShow = LookUpData.statesList.filter((state, i) => {
        return state.countryID === Auth.countryID;
      });

      setStateOfCountry(statesToShow);

      var currentCountrySelected = countries.filter((item, i) => {
        return item.countryID === Auth.countryID;
      });

      phoneFormatReq(currentCountrySelected[0].countryCode);
      handleZipPostalRequirments(currentCountrySelected[0].countryCode);
    } catch {
      //console.log("No states to show");
    }
  };

  const phoneFormatReq = (countryCode) => {
    switch (countryCode) {
      case "US":
      case "USA":
      case "PHL":
      case "IND":
      case "CAN":
      case "MEX":
      case "EGY":
        return setPhoneMinChar(10);
      case "COL":
        return setPhoneMinChar(10);
      case "PAN":
      case "HKG":
        return setPhoneMinChar(8);
      case "POL":
      case "AUS":
        return setPhoneMinChar(9);
      case "TTO":
        return setPhoneMinChar(7);
    }
  };
  const handleZipPostalRequirments = (countryCode) => {
    //console.log(countryCode);
    switch (countryCode) {
      case "US":
      case "USA":
      case "MEX":
        return setZipRequirements({
          accept: "digits",
          required: true,
          length: 5,
        });
      case "IND":
        return setZipRequirements({
          accept: "digits",
          required: true,
          length: 6,
        });

      case "PHL":
      case "AUS":
        return setZipRequirements({
          accept: "digits",
          required: true,
          length: 4,
        });
      case "POL":
        return setZipRequirements({
          accept: "digits",
          required: true,
          length: 5,
        });
      case "CAN":
        return setZipRequirements({
          accept: "letters",
          required: true,
          length: 6,
        });
      case "COL":
        return setZipRequirements({
          accept: "digits",
          required: false,
          length: 6,
        });
      case "PAN":
      case "TTO":
      case "HKG":
        return setZipRequirements({
          accept: "letters",
          required: false,
        });
      case "EGY":
        return setZipRequirements({
          accept: "digits",
          required: false,
          length: 7,
        });
    }
  };

  const onFinish = (values) => {
    setProfileSaveLoading(true);
    submit(values);
  };

  const showTermsModal = (e) => {
    e.preventDefault();
    setIsTermsModalVisible(true);

    if (!termContent && Auth.countryID) {
      reqGetDPCSText(Auth.countryID)
        .then((res) => {
          setTermContent(res.data);
        })
        .catch((err) => {
          console.log(err);
          setTermContent("Unable to load the data privacy statement. Please try again later.");
        });
    }
  };

  const handleCloseTermsModal = (e) => {
    if (e) {
      e.preventDefault();
    }

    setIsTermsModalVisible(false);
  };

  const submit = (values) => {
    var jsonData = {
      candidateID: string_trim(values.candidateId),
      firstName: string_trim(values.firstname),
      middleName: string_trim(values.middlename),
      lastName: string_trim(values.lastname),
      personalEmail: string_trim(values.email),
      countryID: string_trim(values.countryId),
      homeAddress1: string_trim(values.addressLine1),
      homeAddress2: string_trim(values.addressLine2),
      homeAddress3: string_trim(values.addressLine3),
      city: string_trim(values.city),
      stateID: string_trim(values.state),
      zipCode: string_trim(values.postal ? values.postal : null),
      primaryPhone: string_trim(values.phone),
      secondaryPhone: string_trim(values.secondaryPhone),
      currentTitle: string_trim(values.currentTitle),
      currentCompany: string_trim(values.currentCompany),
      preferedContactMethodID: string_trim(values.preferedContactMethod),
      highestLevelOfEducationID: string_trim(values.highestLevelOfEducationID),
      // Mailing address fields
      isPrimaryAddressSameAsMailing: values.isPrimaryAddressSameAsMailing,
      mailingAddress1: string_trim(values.mailingAddress1),
      mailingAddress2: string_trim(values.mailingAddress2),
      mailingAddress3: string_trim(values.mailingAddress3),
      mailingCity: string_trim(values.mailingCity),
      mailingStateID: string_trim(values.mailingStateID),
      mailingZipCode: string_trim(values.mailingZipCode ? values.mailingZipCode : null),
    };

    var jsonStr = JSON.stringify({ ...candidateObj, ...jsonData });

    values.countryName = selectedCountryName;

    reqPostUpdateCandidateProfile(jsonStr)
      .then((res) => {
        setCandidateObj({ ...candidateObj, ...jsonData });
        if (res.data.isSuccess) {
          MsgBox({
            type: "success",
            message: "",
            description: "Profile Updated",
          });

          dispatch(UpdateUser(jsonData));
          setProfileSaveLoading(false);
        } else {
          MsgBox({
            type: "error",
            message: "Error",
            description: res.data.errorMessage,
          });
        }
      })
      .catch((err) => {
        console.log(err);
        setProfileSaveLoading(false);
        GlobalApiErrorHandler({ errors: err.response.data.errors });
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setProfileSaveLoading(false);
    form.scrollToField(errorInfo.errorFields[0].name);
  };

  const getProfileDetails = (getOnlyJobApplications = false) => {
    if (!getOnlyJobApplications) {
      setIsProfileLoading(true);
    } else {
      setIsJobApplicationLoading(true);
    }

    var formData = Auth;
    var formStr = JSON.stringify(formData);

    reqPostRetrieveCandidateProfile(formStr)
      .then((res) => {
        setIsProfileLoading(false);
        setIsJobApplicationLoading(false);
        setCandidateObj({
          ...res.data.candidateObj,
          ...{ applicationList: null },
        });

        var applyList = res.data.candidateObj.applicationList.filter((i) =>
          isAppliedJobs(i.appStatus)
        );
        jobApplicationsCounter(applyList);

        dispatch(UpdateUser(res.data.candidateObj));
        dispatch(SetAppliedJobs(applyList));

        var draftList = res.data.candidateObj.applicationList.filter((i) =>
          isSavedJobs(i.appStatus)
        );
        setJobSavedDraftList(draftList);

        let candidateObj = {
          candidateID: res.data.candidateObj.candidateID,
          personalEmail: res.data.candidateObj.personalEmail,
          firstName: res.data.candidateObj.firstName,
          middleName: res.data.candidateObj.middleName,
          lastName: res.data.candidateObj.lastName,
          currentTitle: "",
          currentCompany: "",
          primaryPhone: res.data.candidateObj.primaryPhone,
          secondaryPhone: res.data.candidateObj.secondaryPhone,
          preferedContactMethodID:
            res.data.candidateObj.preferedContactMethodID,
          homeAddress1: res.data.candidateObj.homeAddress1,
          homeAddress2: res.data.candidateObj.homeAddress2,
          homeAddress3: res.data.candidateObj.homeAddress3,
          city: res.data.candidateObj.city,
          province: "",
          stateID: res.data.candidateObj.stateID,
          zipCode: res.data.candidateObj.zipCode,
          countryID: res.data.candidateObj.countryID,
          countryName: res.data.candidateObj.countryName,
          candidateCoverLetterUniqueID:
            res.data.candidateObj.candidateCoverLetterUniqueID,
          candidateResumeUniqueID:
            res.data.candidateObj.candidateResumeUniqueID,
          candidateCoverLetterFileFriendlyName:
            res.data.candidateObj.candidateCoverLetterFileFriendlyName,
          candidateResumeFileFriendlyName:
            res.data.candidateObj.candidateResumeFileFriendlyName,
          candidateBirthCertificateUniqueID:
            res.data.candidateObj.candidateBirthCertUniqueID,
          candidateBirtCertificateFriendlyName:
            res.data.candidateObj.candidateBirthCertFileFriendlyName,
          candidateCXCUniqueID: res.data.candidateObj.candidateCXCUniqueID,
          candidateCXCFriendlyName:
            res.data.candidateObj.candidateCXCFileFriendlyName,
          highestLevelOfEducationID:
            res.data.candidateObj.highestLevelOfEducationID,
        };

        form.setFieldsValue({
          candidateId: candidateObj.candidateID,
          email: candidateObj.personalEmail,
          firstname: candidateObj.firstName,
          lastname: candidateObj.lastName,
          middlename: candidateObj.middleName,
          phone: candidateObj.primaryPhone,
          secondaryPhone: candidateObj.secondaryPhone,
          preferedContactMethod: candidateObj.preferedContactMethodID,
          countryId: candidateObj.countryID,
          addressLine1: candidateObj.homeAddress1,
          addressLine2: candidateObj.homeAddress2,
          addressLine3: candidateObj.homeAddress3,
          city: candidateObj.city,
          state: candidateObj.stateID,
          postal: candidateObj.zipCode,
          highestLevelOfEducationID: candidateObj.highestLevelOfEducationID,
        });

        if (!getOnlyJobApplications) {
          dispatch(UpdateUser(candidateObj));
        }
      })
      .catch((err) => {
        console.log(err);
        setIsProfileLoading(false);
        setIsJobApplicationLoading(false);
        GlobalApiErrorHandler({ errors: err.response.data.errors });
      });
  };

  return (
    <>
      <div>
        <Row>
          {isBrowser ? (
            <Col md={4} sm={0} xs={0}>
              <BrowserView>
                <Scrollspy
                  currentClassName="isCurrent"
                  className="scrollspy"
                  rootEl=".scrollspy-container"
                  items={[
                    "personal-info",
                    "documents",
                    "jobs-applied",
                    "saved-apply",
                    "main-section",
                  ]}
                >
                  <li>
                    <a href={currentPath + "#personal-info"}>
                      <UserOutlined className="inline-icon" /> Personal
                      Information
                    </a>
                  </li>
                  {candidateObj?.countryCode === 'PAN' || candidateObj?.countryCode === 'TTO' ?
                    ('')
                    :
                    (<li>
                      <a href={currentPath + "#documents"}>
                        <FolderOutlined className="inline-icon" /> Documents
                      </a>
                    </li>)
                  }
                  <li>
                    <a href={currentPath + "#jobs-applied"}>
                      <IdcardOutlined className="inline-icon" />{" "}
                      {jobApplicationCount && jobApplicationCount ? (
                        <>
                          <Badge
                            count={jobApplicationCount}
                            size="small"
                            offset={[10]}
                          >
                            Jobs Applied
                          </Badge>
                        </>
                      ) : (
                        <span>Jobs Applied</span>
                      )}
                    </a>
                  </li>
                  <li>
                    <a href={currentPath + "#saved-apply"}>
                      <ContainerOutlined className="inline-icon" />{" "}
                      {jobSavedDraftList && jobSavedDraftList.length ? (
                        <>
                          <Badge
                            count={jobSavedDraftList.length}
                            size="small"
                            offset={[10]}
                          >
                            Saved Applications
                          </Badge>
                        </>
                      ) : (
                        <span>Saved Application</span>
                      )}
                    </a>
                  </li>
                </Scrollspy>
              </BrowserView>
            </Col>
          ) : (
            <></>
          )}

          <Col md={isBrowser ? 20 : 24} sm={24} xs={24}>
            <Form
              {...layout}
              name="profile"
              initialValues={{
                remember: true,
              }}
              className="label-left-align"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              validateMessages={validateMessages}
              form={form}
              onFieldsChange={(changedFields, allFields) => {
                console.log(changedFields, allFields);
              }}
            >
              <div className="scrollspy-container">
                <section id="personal-info">
                  <PersonalInformation
                    {...statesAsProps}
                    form={form}
                    phoneFormatReq={phoneFormatReq}
                    getLookupData={getLookupData}
                    handleZipPostalRequirments={handleZipPostalRequirments}
                    showEducLvl={true}
                  />
                </section>
                {candidateObj?.countryCode === 'PAN' || candidateObj?.countryCode === 'TTO' ?
                  ('')
                  :
                  (<section id="documents">
                    <DocumentUpload
                      isProfileLoading={isProfileLoading}
                      filenames={false}
                    />
                  </section>)
                }
                <section id="jobs-applied">
                  <JobsApplied
                    isProfileLoading={isProfileLoading}
                    setIsJobApplicationLoading={setIsJobApplicationLoading}
                    isJobApplicationLoading={isJobApplicationLoading}
                    getProfileDetails={getProfileDetails}
                  />
                </section>

                <section id="saved-apply" className="margin-bottom">
                  <SavedApplication
                    isProfileLoading={isProfileLoading}
                    setIsJobApplicationLoading={setIsJobApplicationLoading}
                    isJobApplicationLoading={isJobApplicationLoading}
                    getProfileDetails={getProfileDetails}
                    jobSavedDraftList={jobSavedDraftList}
                  />
                </section>
              </div>

              <Row justify="center">
                <Col sm={23} xs={24}>
                  <Card bordered={false}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <span>Terms of Use: <a href="/" onClick={showTermsModal}>Data Privacy</a></span>
                      </div>
                      <Button
                        type="danger"
                        loading={profileSaveLoading}
                        onClick={() => {
                          form.submit();
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>

      <Modal
        title="Data Privacy Consent Statement"
        visible={isTermsModalVisible}
        onCancel={handleCloseTermsModal}
        className="terms-modal"
        destroyOnClose
        width={850}
        footer={null}
      >
        <div id="terms-content">
          {termContent ? <div dangerouslySetInnerHTML={{ __html: termContent }}></div> : <Spin />}
        </div>
      </Modal>
    </>
  );
};
